import { TextLink } from "components/Clickable/Links";
import { PDPProductCodeConfigurator } from "components/ProductCodeConfigurator/PDPProductCodeConfigurator";
import { FunctionList } from "components/ProductFunctions/FunctionList";
import { Tab, TabPanel, Tabs } from "components/Tabs";
import urls from "helpers/urls";
import { notEmptyOrBlank } from "helpers/utils";
import {
    AttributeOptionDetails,
    AttributeOptionPlusInfo,
} from "interfaces/products";

import styles from "./product-detail-tabs.module.scss";

// Rendered all the `ul` with `role="list"` to work around a safari bug where
// screen readers will not recognize lists styled using `list-style: none`
export function ProductDetailTabs({
    attribute,
    selectedOptions,
    loading,
    setLoading,
}: {
    attribute: AttributeOptionDetails;
    selectedOptions: AttributeOptionPlusInfo[];
    loading: boolean;
    setLoading: (loading: boolean) => void;
}) {
    const hasInstallationInstructions =
        attribute.installation_instructions &&
        attribute.installation_instructions.length > 0;
    const hasTemplates = attribute.templates && attribute.templates.length > 0;
    const showInstallationTab = hasInstallationInstructions || hasTemplates;

    const hasTechSpecs = !!attribute.technical_specs;
    const hasSpecifications =
        attribute.specifications && attribute.specifications.length > 0;
    const hasWarnings = attribute.warnings.length > 0;
    const showTechSpecsTab = hasTechSpecs || hasSpecifications || hasWarnings;

    const tabs = [
        { text: "Description", className: "description" },
        showTechSpecsTab
            ? { text: "Tech Specs", className: "resources" }
            : null,
        showInstallationTab
            ? { text: "Installation Guides", className: "guides" }
            : null,
        { text: "Product Options", className: "product-options" },
    ].filter(notEmptyOrBlank);

    return (
        <Tabs
            className={styles.container}
            tabs={
                <>
                    {tabs.map((tab, index) => (
                        <Tab
                            className={`al-pdp__product-tab al-pdp__product-tab--${tab.className}`}
                            index={index}
                            key={index}
                        >
                            {tab.text}
                        </Tab>
                    ))}
                </>
            }
            panels={
                <>
                    <TabPanel>
                        <div className={styles.panel}>
                            <PimRichText text={attribute.description} />
                            <div className={styles.secondaryColumn}>
                                {attribute.functions.length > 0 && (
                                    <FunctionList
                                        functions={attribute.functions}
                                    />
                                )}
                                {attribute.upgrades.length > 0 && (
                                    <section>
                                        <h2 className={styles.heading}>
                                            Available Upgrades
                                        </h2>
                                        <ul role="list">
                                            {attribute.upgrades.map(
                                                (upgrade) => (
                                                    <li key={upgrade.id}>
                                                        {upgrade.option}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </section>
                                )}
                            </div>
                        </div>
                    </TabPanel>
                    {showTechSpecsTab && (
                        <TabPanel>
                            <div className={styles.panel}>
                                <PimRichText text={attribute.technical_specs} />
                                <section className={styles.resources}>
                                    <h2 className={styles.heading}>
                                        Specifications
                                    </h2>
                                    <ul role="list">
                                        {attribute.specifications?.map(
                                            (resource) => (
                                                <li key={resource.file}>
                                                    <a
                                                        href={resource.file}
                                                        download={resource.file.replace(
                                                            /^.*(\.\w+)$/,
                                                            `${resource.title}$1`,
                                                        )}
                                                    >
                                                        {resource.title}
                                                    </a>
                                                </li>
                                            ),
                                        )}
                                        {attribute.warnings.map((warning) => {
                                            if (warning.option.includes("65")) {
                                                return (
                                                    <li key={warning.id}>
                                                        <TextLink
                                                            href={urls.prop65}
                                                        >
                                                            {warning.option}
                                                        </TextLink>
                                                    </li>
                                                );
                                            } else {
                                                return (
                                                    <li key={warning.id}>
                                                        {warning.option}
                                                    </li>
                                                );
                                            }
                                        })}
                                    </ul>
                                </section>
                            </div>
                        </TabPanel>
                    )}
                    {showInstallationTab && (
                        <TabPanel>
                            <div className={styles.panel}>
                                <section className={styles.installation}>
                                    <ul role="list">
                                        {[
                                            ...(attribute.installation_instructions ??
                                                []),
                                            ...(attribute.templates ?? []),
                                        ].map((resource) => (
                                            <li key={resource.file}>
                                                <a
                                                    href={resource.file}
                                                    download={resource.file.replace(
                                                        /^.*(\.\w+)$/,
                                                        `${resource.title}$1`,
                                                    )}
                                                >
                                                    {resource.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            </div>
                        </TabPanel>
                    )}
                    <TabPanel>
                        <PDPProductCodeConfigurator
                            attribute={attribute}
                            selectedOptions={selectedOptions}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    </TabPanel>
                </>
            }
        />
    );
}

/**
 * From Josh:
 * The PIM sends plain text with some html elements in it. There is no spec. So far i
 * have only observed `<li>` elements coming through (which are useless on their own).
 * The primary goal of this code is to not cover additional edge cases if possible:
 * if we start seeing additional html elements we should push back and try to devise a
 * better system (like the one the PIM uses for other Assa Abloy sites)
 */
export function PimRichText({ text }: { text?: string | null }) {
    if (!text) return null;
    const lines = text.split(/\n/);
    const result: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (line === "") {
            result.push(<br key={i} />);
            i++;
        } else if (isListItem(line)) {
            const listItems = pickUntil(
                lines.slice(i),
                (line) => !isListItem(line),
            );
            result.push(
                <ul role="list" key={i}>
                    {listItems.map((item, index) => {
                        /**
                         * Strip out indicators that the item is a list item:
                         * * any leading dashes/spaces
                         * * start of line opening tag of an li
                         * * end of line closing tag of an li
                         */
                        const text = item.replace(/(^-\s*|^<li>|<\/li>$)/g, "");
                        return <li key={`${i}-${index}`}>{text}</li>;
                    })}
                </ul>,
            );
            i += listItems.length;
        } else {
            result.push(<p key={i}>{line}</p>);
            i++;
        }
    }
    return <div className={styles.description}>{result}</div>;
}

function isListItem(line: string) {
    return /^(<li>|-)/.test(line);
}

function pickUntil<T>(ary: T[], predicate: (arg: T) => boolean): T[] {
    const firstCorrect = ary.findIndex((item) => predicate(item));
    if (firstCorrect === -1) return ary.slice(0);
    return ary.slice(0, firstCorrect);
}
